import type { AnalyticsToolResult } from "../../../../server/services/analytics/types";
import type { AdCopyVariation } from './canvas-store';

export type CanvasTool = 'select' | 'hand' | 'brush' | 'comment' | 'mark';

export type CanvasView = 'new-task' | 'project' | 'heartbeat' | 'chat-only' | 'chat-canvas' | 'brands' | 'analytics' | 'ads-manager' | 'ad-templates' | 'ad-library' | 'assets';

export interface CanvasImage {
  id: string;
  name: string;
  src: string;
  x: number;
  y: number;
  displayWidth: number;
  displayHeight: number;
  originalWidth: number;
  originalHeight: number;
  colorIndex: number;
  prompt?: string;
  isFavorite?: boolean;
  isEditVersion?: boolean;
  sourceImageId?: string;
  // v4.19 — funnel-stage frame derivation (Preview sections)
  adAngleId?: number | null;
  workflowRunId?: number | null;
  /** Playable tile (video-ad-clone): src = poster frame, videoUrl = mp4.
   *  Server-owned like workflowRunId — MUST be mapped through every image
   *  rebuild/persist path or the destructive /images/sync strips it. */
  videoUrl?: string | null;
  adStyle?: string;
  adAngle?: string;
  wavespeedPrompt?: string;
  conceptName?: string;
  headline?: string;
  bodyCopy?: string;
  ctaText?: string;
  /** QA result persisted on the image row. Used as fallback for the
   *  on-canvas badge when the in-memory imageQaBadges map doesn't have
   *  the live SSE-delivered badge (e.g. after page reload or when SSE
   *  raced ahead of the requestId→url mapping). */
  qaPassed?: boolean | null;
  qaReason?: string | null;
}

export interface CanvasMark {
  id: string;
  imageId: string;
  regionX: number;
  regionY: number;
  regionWidth: number;
  regionHeight: number;
  instruction: string;
  isPending: boolean;
}

export interface CanvasComment {
  id: string;
  imageId: string;
  pinX: number;
  pinY: number;
  text: string;
  isPending: boolean;
  isFailed?: boolean;
  author: string;
}

export interface CanvasProject {
  id: string;
  name: string;
  imageCount: number;
  color: string;
}

export interface CanvasTask {
  id: string;
  label: string;
  status: 'done' | 'running' | 'failed' | 'pending';
  isCanvasOpen?: boolean;
}

export interface SubTask {
  id: string;
  icon: 'search' | 'save' | 'generate' | 'write' | 'analyze';
  label: string;
  status: 'pending' | 'running' | 'completed';
}

export interface TaskStep {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  isExpanded?: boolean;
  description?: string;
  subTasks?: SubTask[];
}

export interface ChatArtifact {
  title: string;
  type: 'document' | 'brief' | 'copy' | 'analysis';
  content: string;
}

export interface ResearchTaskStep {
  id: string;
  tool: string;
  description: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  summary?: string;
  substeps?: string[];
  durationMs?: number;
}

export interface ResearchTaskPlan {
  title: string;
  brandName: string;
  brandUrl?: string;
  steps: ResearchTaskStep[];
  estimatedDuration: string;
}

export interface ActiveResearchTask {
  taskId: number;
  plan: ResearchTaskPlan;
  status: 'planning' | 'running' | 'completed' | 'failed';
  completedSteps: number;
  totalSteps: number;
  documentReady: boolean;
  followUps: string[];
  errorMessage?: string;
  startedAt?: number;
  documentPreview?: string;
  documentWordCount?: number;
}

export type IntentType = 'conversation' | 'creation' | 'research' | 'analysis' | 'edit' | 'admap';

export interface AdMapAngle {
  id: string;
  name: string;
  description: string;
  emotion: string;
}

export interface AdMapDesign {
  id: string;
  name: string;
  format: string;
  hook: string;
  copy: string;
  cta: string;
  angles: AdMapAngle[];
}

export interface AdMapAdSet {
  id: string;
  name: string;
  targeting: string;
  budget: string;
  adDesigns: AdMapDesign[];
}

export interface AdMapCampaign {
  id: string;
  name: string;
  objective: string;
  budget: string;
  duration: string;
  platforms: string[];
  adSets: AdMapAdSet[];
}

export interface AdMapData {
  campaign: AdMapCampaign;
}

export interface AdMapRecord {
  id: number;
  projectId: number;
  userId: number;
  brandId: number | null;
  adAccountId: number | null;
  name: string;
  status: 'draft' | 'in_review' | 'approved' | 'generating' | 'completed';
  mapData: AdMapData;
  canvasX: number | null;
  canvasY: number | null;
  createdAt: string;
  updatedAt: string;
  approvedAt: string | null;
}

export interface CanvasPlaceholder {
  taskId: string;
  x: number;
  y: number;
  width: number;
  height: number;
  prompt?: string;
  genWidth?: number;
  genHeight?: number;
  filename?: string;
  failed?: boolean;
  errorMsg?: string;
  resolving?: boolean;
  /** Set on intent_resolved placeholders so canvas_image_pending only
   *  claims slots from the SAME batch, and playbook_complete only clears
   *  orphans from THIS batch. Without batching, multiple concurrent
   *  "make N ads" requests cross-contaminate (image from batch C lands
   *  in batch A's slot, and batch C's playbook_complete wipes batch A's
   *  loading placeholders). */
  batchId?: string;
  /** Stable workflow name for this batch, independent of subsequent runs. */
  batchLabel?: string;
  workflowRunId?: number;
  /** v4.64 — the tile's fixed grid slot within its batch (server conceptIndex).
   *  Set on intent boxes at creation and carried onto the agent-img
   *  placeholder at claim time, so every repack keeps the tile in ITS slot
   *  instead of reshuffling by arrival order. */
  slotIndex?: number;
}

export interface ChatImageSlot {
  index: number;
  resolved?: string;
  failed?: boolean;
  errorMsg?: string;
}

export interface WebResearchSource {
  url: string;
  title: string;
  snippet: string;
  domain: string;
  favicon: string;
  status: 'pending' | 'success' | 'failed' | 'skipped';
}

export interface AgentActionCard {
  id: string;
  toolName: string;
  friendlyLabel: string;
  status: 'running' | 'success' | 'error';
  summary?: string;
  error?: string;
}

export interface AgentTaskStep {
  id: string;
  toolName: string;
  friendlyLabel: string;
  status: 'running' | 'success' | 'error';
  summary?: string;
  error?: string;
  detail?: string;
  durationMs?: number;
  contextDetail?: string;
}

export interface AgentPlanStep {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
}

export interface AgentPlan {
  id: string;
  title?: string;
  steps: AgentPlanStep[];
}

export interface QAStatus {
  stepId: string;
  status: 'reviewing' | 'approved' | 'regenerating' | 'skipped';
  attempt: number;
  label: string;
  passed?: boolean;
  issues?: string[];
  issueCategory?: string;
}

export interface AgentTaskGroup {
  id: string;
  label: string;
  status: 'pending' | 'active' | 'completed' | 'failed';
  steps: AgentTaskStep[];
  iterationIndex: number;
  qaStatus?: QAStatus;
}

export interface AgentDocument {
  title: string;
  content: string;
  summary: string;
}

export interface AdCardData {
  id: string;
  name: string;
  thumbnail_url: string;
  status?: string;
  headline?: string;
  adCopy?: string;
  cta?: string;
  campaignName?: string;
  adSetName?: string;
  videoUrl?: string;
  metrics: {
    ctr?: number;
    cpc?: number;
    roas?: number;
    spend?: number;
    impressions?: number;
    clicks?: number;
    conversions?: number;
    cvr?: number;
  };
}

export interface WorkflowPhaseInfo {
  index: number;
  name: string;
}

export interface WorkflowNudgeOption {
  label: string;
  value: string;
  action?: string;
}



export interface WorkflowCheckpointPayload {
  campaignTheme?: string;
  angles?: Array<{
    name: string;
    hook: string;
    messagingStrategy: string;
    targetEmotion: string;
    creativeDirection: string;
    exampleHeadline: string;
    exampleBodyCopy: string;
  }>;
  recommendedAngle?: number;
  rationale?: string;
  images?: Array<{
    briefId: string;
    imageUrl: string;
    title: string;
    adCopy: string;
    headline: string;
    cta: string;
  }>;
  [key: string]: unknown;
}

export interface WorkflowState {
  workflowRunId?: number;
  skeletonKey?: string;
  name?: string;
  description?: string;
  estimatedMinutes?: number;
  phases?: WorkflowPhaseInfo[];
  status?: 'confirming' | 'running' | 'paused' | 'completed' | 'cancelled' | 'error';
  currentPhaseIndex?: number;
  currentPhaseName?: string;
  totalPhases?: number;
  checkpointId?: number;
  checkpointType?: 'strategy_review' | 'gallery_review' | string;
  checkpointPayload?: WorkflowCheckpointPayload;
  errorMessage?: string;
  originalMessage?: string;
}

export interface WorkflowNudge {
  message: string;
  options: WorkflowNudgeOption[];
  skeletonType?: string;
  skeletonName?: string;
  originalMessage?: string;
}

export interface WorkflowTaskCardData {
  taskId: string;
  label: string;
  toolName: string;
  status: 'running' | 'completed' | 'failed';
  progressMessage?: string;
  summary?: string;
  thumbnails?: string[];
  error?: string;
}

/** v7.43 — durable concepts-run state the SERVER writes onto the task's
 *  conversation entry (keyed by the client's assistant message id) at run
 *  start / progress / success / failure / interruption. Lets a reopened task
 *  show the run's real outcome instead of a "turn didn't complete" marker.
 *  See conversation-hydrate.ts for how each status renders. */
export interface ConceptsRunState {
  status: 'generating' | 'ready' | 'failed' | 'interrupted';
  setId: number | null;
  count: number;
  startedAt: number;
  retryMessage?: string;
  adFilter?: { styles: string[] | null; angles: string[] | null };
}

export interface CanvasChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  /** v7.43 — server-owned concepts-run memory (see ConceptsRunState). */
  conceptDraftProgress?: {
    stage: 'writing' | 'validating' | 'saved';
    drafted: number;
    target: number;
    kept: number;
    conceptNames: string[];
    updatedAt: number;
  };
  conceptsRun?: ConceptsRunState;
  thumbnails?: string[];
  actionPills?: string[];
  aspectRatio?: string;
  imageSlots?: ChatImageSlot[];
  taskSteps?: TaskStep[];
  timestamp?: number;
  thinkingText?: string;
  intent?: IntentType;
  researchSummary?: string;
  webResearchSources?: WebResearchSource[];
  webResearchStatus?: 'searching' | 'analyzing' | 'complete';
  webResearchReport?: string;
  isAgentMessage?: boolean;
  agentCards?: AgentActionCard[];
  agentTaskGroups?: AgentTaskGroup[];
  agentThinking?: string;
  agentDocument?: AgentDocument;
  agentPlan?: AgentPlan;
  sidebarDoc?: { title: string; content: string };
  /** v5.61 — clickable Video Script card (opens the video_script sidebar). */
  videoScriptCard?: { draftId: number; title: string };
  /** v6.77 — clickable Angle Set card (opens the angle_set Angles doc sidebar). */
  angleSetCard?: { setId: number; title: string; angleCount?: number };
  /** v6.05 — chat-edit flow: "Generate the video" action button card. */
  videoGenerateCard?: { draftId: number; title: string };
  adCards?: AdCardData[];
  toolResults?: Array<{ toolName: string; renderCard: AnalyticsToolResult["renderCard"]; analysis: AnalyticsToolResult["analysis"]; heartbeatLinks: AnalyticsToolResult["heartbeatLinks"] }>;
  workflowState?: WorkflowState;
  workflowNudge?: WorkflowNudge;
  workflowThinkingFeed?: WorkflowThinkingEntry[];
  playbookProgress?: PlaybookProgressState;
  classifierReasoning?: string;
  ackText?: string;
  classificationTimeMs?: number;
  workflowTaskCards?: WorkflowTaskCardData[];
  workflowPlanTitle?: string;
  workerProgress?: WorkerProgressEntry[];
  suggestionPills?: { question: string; pills: Array<{ label: string; description?: string; value: string }> };
}

export interface WorkerProgressEntry {
  id: string;
  name: string;
  status: 'running' | 'completed' | 'failed';
  durationMs?: number;
}

export interface WorkflowThinkingEntry {
  id: string;
  type: 'thinking' | 'tool_start' | 'tool_result' | 'text';
  text: string;
  toolName?: string;
  timestamp: number;
}

export interface PlaybookPhaseInfo {
  id: string;
  name: string;
  phaseType: string;
}

export type PlaybookPhaseStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface PlaybookQaResult {
  conceptIndex: number;
  attempt: number;
  score: number;
  passed: boolean;
  status: 'passed' | 'regenerating' | 'needs_review';
}

export interface PlaybookProgressState {
  playbookName: string;
  totalPhases: number;
  phases: PlaybookPhaseInfo[];
  phaseStatuses: Record<string, PlaybookPhaseStatus>;
  phaseStatusTexts: Record<string, string>;
  activePhaseId: string | null;
  qaResults: PlaybookQaResult[];
  conceptProgress: { current: number; total: number } | null;
  completedSummary: string | null;
  isDone: boolean;
  campaignBrief?: { briefId: string; title: string; content: string } | null;
  /** Whether Brand Identity Lock was actually applied to this run (toggle ON +
   *  brand analyzed). undefined for older messages saved before this existed. */
  brandIdentityLock?: boolean;
}

export interface ContextMenuState {
  x: number;
  y: number;
  type: 'image' | 'canvas';
  imageId?: string;
}

export interface CommentPopoverState {
  imageId: string;
  pinX: number;
  pinY: number;
  screenX: number;
  screenY: number;
}

export interface MarkPopoverState {
  imageId: string;
  regionX: number;
  regionY: number;
  regionWidth: number;
  regionHeight: number;
  screenX: number;
  screenY: number;
}

export interface CanvasSettings {
  showGrid: boolean;
  snapToGrid: boolean;
  showGuides: boolean;
}

export type UndoAction =
  | {
      type: 'MOVE';
      updates: { id: string; fromX: number; fromY: number; toX: number; toY: number }[];
    }
  | {
      type: 'RESIZE';
      id: string;
      fromW: number;
      fromH: number;
      toW: number;
      toH: number;
      fromX?: number;
      fromY?: number;
      toX?: number;
      toY?: number;
    }
  | {
      type: 'DELETE';
      images: CanvasImage[];
    }
  | {
      type: 'ADD';
      images: CanvasImage[];
    }
  | {
      type: 'DUPLICATE';
      images: CanvasImage[];
    }
  | {
      type: 'ADD_COMMENT';
      comment: CanvasComment;
    }
  | {
      type: 'DELETE_COMMENT';
      comment: CanvasComment;
    }
  | {
      type: 'APPLY_COMMENTS';
      commentIds: string[];
    };

export interface CanvasInlineChart {
  type: 'bar' | 'line';
  title: string;
  labels: string[];
  values: number[];
}

export interface CanvasGenerationRow {
  id: string;
  batchIndex: number;
  prompt: string;
  brandId?: number;
  imageIds: string[];
  rowY: number;
  rowHeight: number;
  isEditRow?: boolean;
  editRowLabel?: string;
  sourceImageIds?: string[];
}

export interface PendingEdit {
  imageId: string;
  imageSrc: string;
  comments: CanvasComment[];
  prompt?: string;
}

/**
 * Per-project state. Lives in state.projects[projectId].
 * Single source of truth for everything scoped to one canvas chat.
 */
export interface ProjectState {
  // Identity & loading
  projectId: number;
  loadedAt: number | null;          // null = never loaded from DB
  loading: boolean;                 // per-project DB-fetch in flight

  // Conversation
  messages: CanvasChatMessage[];
  conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>;
  isTyping: boolean;
  agentThinking: string | null;

  // Canvas
  images: CanvasImage[];
  imageQaBadges: Record<string, { passed: boolean; score: number; badgeType: string; reason: string; attempt: number }>;
  imageRequestIdToUrl: Record<string, string>;
  canvasAdCopy: Record<string, { variations: AdCopyVariation[]; activeVariationIndex: number }>;
  canvasPlaceholders: Record<string, CanvasPlaceholder>;
  generationRows: CanvasGenerationRow[];
  hasPersistedImages: boolean;

  // Workflow / playbook
  playbookProgress: PlaybookProgressState | null;
  workflowState: WorkflowState | null;
  activeWorkflowRunId: number | null;

  // Per-project view state
  isCanvasOpen: boolean;
  activeView: CanvasView;

  // Pending UI cues
  pendingPanTarget: { x: number; y: number } | null;
  pendingFitToView: boolean;
  pendingFitToRow: { minX: number; minY: number; maxX: number; maxY: number } | null;

  // Per-project metadata (cached on first load)
  brandId: number | null;
  adAccountId: number | null;
  parentId: number | null;
}

/**
 * Default per-project state factory. Used when creating a fresh project entry
 * before DB load completes, or when a brand-new chat is started.
 */
export function defaultProjectState(projectId: number): ProjectState {
  return {
    projectId,
    loadedAt: null,
    loading: false,
    messages: [],
    conversationHistory: [],
    isTyping: false,
    agentThinking: null,
    images: [],
    imageQaBadges: {},
    imageRequestIdToUrl: {},
    canvasAdCopy: {},
    canvasPlaceholders: {},
    generationRows: [],
    hasPersistedImages: false,
    playbookProgress: null,
    workflowState: null,
    activeWorkflowRunId: null,
    isCanvasOpen: false,
    activeView: 'chat-only',
    pendingPanTarget: null,
    pendingFitToView: false,
    pendingFitToRow: null,
    brandId: null,
    adAccountId: null,
    parentId: null,
  };
}
